import { Tile, TileType, LevelConfig } from './types.js';
import { TILE_TYPES, GAME_CONFIG } from './constants.js';

// 生成 UUID 的辅助函数
const generateId = () => Math.random().toString(36).substr(2, 9);

// 检查方块 A 是否被方块 B 覆盖
export const isCovered = (tileA, tileB) => {
  // 我们使用 zIndex 来表示渲染顺序/视觉深度。
  // 如果 B 在堆叠顺序中位于 A 之下或相同，则无法覆盖 A。
  if (tileB.zIndex <= tileA.zIndex) return false;

  // 简单的边界框相交逻辑，针对“牌堆”感觉进行了调整
  // 方块大约是 1x1 单位。重叠阈值比较宽松。
  const overlapThreshold = 0.85; 
  const dx = Math.abs(tileA.x - tileB.x);
  const dy = Math.abs(tileA.y - tileB.y);

  return dx < overlapThreshold && dy < overlapThreshold;
};

export const updateClickableStatus = (tiles) => {
  return tiles.map(tile => {
    // 如果有任何其他方块覆盖它，则该方块不可点击
    const isBlocked = tiles.some(other => isCovered(tile, other));
    return { ...tile, isClickable: !isBlocked };
  });
};

export const generateLevel = (config) => {
  let tiles = [];
  const { tileCount, typeCount } = config;
  const totalTriples = tileCount / 3;
  
  // 1. 创建类型池（确保 3 个一组）
  let availableTypes = TILE_TYPES.slice(0, typeCount);
  let typePool = [];
  
  for (let i = 0; i < totalTriples; i++) {
    const type = availableTypes[i % availableTypes.length];
    typePool.push(type, type, type);
  }
  
  // 打乱类型
  typePool = typePool.sort(() => Math.random() - 0.5);

  // 2. 动态生成位置
  // 根据方块数量决定层数，方块越多，层数越高
  // 基础层数3，每增加30个方块增加1层
  const maxLayers = 3 + Math.floor(tileCount / 30);
  
  const positions = [];
  
  // 我们生成比需求多得多的候选位置，然后进行裁剪
  // 这样可以保证即使在高难度下也有足够的堆叠位置
  
  for (let layer = 0; layer <= maxLayers; layer++) {
    // 每一层的网格大小逐渐减小，形成金字塔结构
    // Layer 0: 范围大 (例如 -3 到 3)
    // Layer Max: 范围小 (例如 -0.5 到 0.5)
    
    // 随着层数增加，范围收缩
    const range = Math.max(0.5, 3.5 - (layer * 0.6));
    const step = 0.5; // 网格步长，0.5 意味着会有半格重叠

    for(let x = -range; x <= range; x += step) {
      for(let y = -range * 1.2; y <= range * 1.2; y += step) {
        // 每一层有一定的随机性是否放置方块，层数越高越稀疏，但为了难度可以保持一定密度
        // 基础概率 0.6，随层数增加略微降低，形成不规则形状
        const probability = Math.max(0.3, 0.7 - (layer * 0.05));
        
        // 添加一些随机扰动，让布局看起来不那么死板
        if (Math.random() < probability) {
          positions.push({ x, y, layer });
        }
      }
    }
    
    // 额外的“特殊堆叠”：在中心区域随机增加高层位置，制造“难点”
    if (layer > 1) {
        for(let k=0; k<3; k++) {
             positions.push({ 
                 x: (Math.random() * 2) - 1, 
                 y: (Math.random() * 2) - 1, 
                 layer: layer 
             });
        }
    }
  }

  // 3. 筛选位置
  // 我们需要正好 typePool.length 个位置
  // 排序策略：
  // 优先低层 (layer)，其次优先中心 (接近 0,0)
  // 这样保证底部填满，上面慢慢堆叠
  positions.sort((a, b) => {
     if (a.layer !== b.layer) return a.layer - b.layer;
     const distA = Math.abs(a.x) + Math.abs(a.y);
     const distB = Math.abs(b.x) + Math.abs(b.y);
     return distA - distB;
  });

  // 如果生成的位置不够（极少情况），补充 (0,0) 位置堆叠
  let selectedPositions = positions.slice(0, typePool.length);
  while (selectedPositions.length < typePool.length) {
    const layer = selectedPositions.length > 0 
        ? selectedPositions[selectedPositions.length - 1].layer + 1 
        : 0;
    selectedPositions.push({ x: 0, y: 0, layer: layer });
  }
  
  // 再次打乱位置分配给类型的顺序？
  // 不，通常位置是固定的结构，类型是随机的。我们只需将 typePool 映射到 selectedPositions
  // 但是，为了让高的牌不总是同一种类型（虽然 typePool 已经 shuffle 了），直接映射即可。

  // 4. 将类型分配给位置
  tiles = selectedPositions.map((pos, index) => ({
    id: generateId(),
    type: typePool[index],
    x: pos.x,
    y: pos.y,
    layer: pos.layer,
    isClickable: true, // 将在之后立即计算
    zIndex: index // 使用严格递增的索引作为 zIndex 以确保正确的重叠逻辑
  }));

  // 确保 tiles 按 zIndex 排序（虽然 map 顺序已经是这样，但为了保险）
  tiles.sort((a, b) => a.zIndex - b.zIndex);

  return updateClickableStatus(tiles);
};